import { Injectable } from '@angular/core';
import { CommunicationManagerService } from './communication-manager.service';
import { filter, of, switchMap, forkJoin, map } from 'rxjs';
import { LocalStorageService } from './storage.service';
import {
  PricingPlan,
  PricingPlanMapped,
  StripeStatus,
  UserSubscription,
  RecurringIntervalPrice,
} from '../models/subscription-plan.model';
import {
  StripeProductAddon,
  MappedAddOn,
  PriceDetails,
} from '../models/stripe-product-addons.model';
import { StripeHistoryInvoice } from '../models/billing-history.model';
import { Miscellanious } from '@ops/global-config';

@Injectable({
  providedIn: 'root',
})
export class StripeService {
  constructor(
    private cm: CommunicationManagerService,
    private localStorageService: LocalStorageService
  ) {}

  addAddOnOnSubscriptionId(subscriptionId: string, priceId: string) {
    const payload = {
      priceId,
    };
    return this.cm.post(`stripe/subscription/${subscriptionId}/addon`, payload);
  }

  getStripePaymentMethods() {
    return this.cm.get(`stripe/payment-methods`).pipe(
      map((paymentMethods: any[]) => {
        return paymentMethods.map((pm: any) => ({
          id: pm.id,
          isDefault: pm.isDefault || false,
          cardOwner: pm.billing_details?.name || pm.billing_details?.email || 'N/A',
          funding: pm.card?.funding || '',
          brand: pm.card?.brand || '',
          last4: pm.card?.last4 || '',
          expirationDate: pm.card?.exp_month && pm.card?.exp_year
            ? `${String(pm.card.exp_month).padStart(2, '0')} / ${pm.card.exp_year}`
            : '',
          expired: pm.card?.exp_month && pm.card?.exp_year
            ? new Date(pm.card.exp_year, pm.card.exp_month, 1) < new Date()
            : false,
        }));
      })
    );
  }

  setStripePaymentMethodDefault(stripePaymentMethodId: string, stripeSubscriptionId: string) {
    const payload = {
      stripePaymentMethodId,
      stripeSubscriptionId,
    };
    return this.cm.post(`stripe/payment-method/default`, payload);
  }

  addStripePaymentMethod() {
    return this.cm.get(`stripe/add-payment-method`);
  }

  removeAddOnFromSubscriptionId(subscriptionId: string, priceId: string) {
    const payload = {
      priceId,
    };
    return this.cm.post(
      `stripe/subscription/${subscriptionId}/remove-addon`,
      payload
    );
  }

  getBillingHistory(subscriptionId: string) {
    return this.cm.get(`stripe/billingHistory/${subscriptionId}`).pipe(
      switchMap((stripeHistoryItems: StripeHistoryInvoice[]) => {
        const reMappedHistoryItems = stripeHistoryItems.map(
          (mappedHistoryItem) => {
            const paidAtTs =
              (mappedHistoryItem.status_transitions?.paid_at || 1) * 1000;
            const paidAtDate = new Date(paidAtTs);
            const createdTs =
              (mappedHistoryItem.created || 1) * 1000;
            const createdDate = new Date(createdTs);
            const currencyExchangeSymbol = Miscellanious.currencySymbolFromCode(
              'en-US',
              mappedHistoryItem.currency
            );

            return {
              id: mappedHistoryItem.id,
              status: mappedHistoryItem.status,
              currencyExchangeSymbol: currencyExchangeSymbol,
              paid_at: paidAtDate,
              created: createdDate,
              total:
                (mappedHistoryItem.total || 0) /
                (mappedHistoryItem.currency?.toUpperCase().toString() === 'JPY'
                  ? 1
                  : 100),
              hosted_invoice_url: mappedHistoryItem.hosted_invoice_url,
            };
          }
        );

        return of(reMappedHistoryItems);
      })
    );
  }

  getStripeProducts() {
    const localStripeProducts = this.localStorageService.getAllStripeProducts();
    if (localStripeProducts.length > 0) {
      const localStripeProductsSorted = localStripeProducts.sort(
        (a: any, b: any) => {
          return a.yearly.price - b.yearly.price;
        }
      );

      return of(localStripeProductsSorted);
    } else {
      return forkJoin({
        products: this.cm.get(`stripe/products`),
        addons: this.cm.get(`stripe/products/addons`),
      }).pipe(
        switchMap(({ products: subPlans, addons: stripeProductAddons }) => {
          // First map the addons to a lookup structure grouped by interval and currency
          const addonMap = stripeProductAddons.reduce(
            (
              acc: Record<
                string,
                {
                  monthly: Record<string, RecurringIntervalPrice[]>;
                  yearly: Record<string, RecurringIntervalPrice[]>;
                }
              >,
              addon: StripeProductAddon
            ) => {
              if (!addon?.deletedAt) {
                // Initialize the structure for this product if it doesn't exist
                if (!acc[addon.stripeProductId]) {
                  acc[addon.stripeProductId] = {
                    monthly: {},
                    yearly: {},
                  };
                }

                // Process all prices to find yearly and monthly options
                addon.stripePrices.forEach((price: any) => {
                  if (price.active) {
                    const currency = price.currency.toUpperCase();
                    const interval = price.recurring.interval;
                    const addonPrice: RecurringIntervalPrice = {
                      id: price.id,
                      price:
                        (price.unit_amount || 100) /
                        (price?.currency?.toUpperCase().toString() === 'JPY'
                          ? 1
                          : 100),
                      currency: currency,
                      metadata: price.metadata || {},
                    };

                    // Add to the appropriate interval and currency group
                    if (interval === 'month') {
                      if (!acc[addon.stripeProductId].monthly[currency]) {
                        acc[addon.stripeProductId].monthly[currency] = [];
                      }
                      acc[addon.stripeProductId].monthly[currency].push(
                        addonPrice
                      );
                    } else if (interval === 'year') {
                      if (!acc[addon.stripeProductId].yearly[currency]) {
                        acc[addon.stripeProductId].yearly[currency] = [];
                      }
                      acc[addon.stripeProductId].yearly[currency].push(
                        addonPrice
                      );
                    }
                  }
                });
              }
              return acc;
            },
            {}
          );

          // Map the products and include addon prices
          const pricingPlanMapped = subPlans.map((subPlanItem: PricingPlan) => {
            // Group prices by interval and currency
            const monthlyPrices: Record<string, RecurringIntervalPrice> = {};
            const yearlyPrices: Record<string, RecurringIntervalPrice> = {};

            subPlanItem.stripePrices.forEach((priceItem: any) => {
              if (
                priceItem.recurring.interval === 'month' &&
                priceItem.active
              ) {
                monthlyPrices[priceItem.currency.toUpperCase()] = {
                  id: priceItem.id,
                  price:
                    (priceItem.unit_amount || 100) /
                    (priceItem.currency?.toUpperCase().toString() === 'JPY'
                      ? 1
                      : 100),
                  metadata: priceItem.metadata,
                  currency: priceItem.currency.toUpperCase(),
                };
              } else if (
                priceItem.recurring.interval === 'year' &&
                priceItem.active
              ) {
                yearlyPrices[priceItem.currency.toUpperCase()] = {
                  id: priceItem.id,
                  price:
                    (priceItem.unit_amount || 100) /
                    (priceItem.currency?.toUpperCase().toString() === 'JPY'
                      ? 1
                      : 100),
                  metadata: priceItem.metadata,
                  currency: priceItem.currency.toUpperCase(),
                };
              }
            });

            // Apply addon prices to all matching currencies and intervals
            Object.entries(monthlyPrices).forEach(([currency, price]) => {
              // Find all addon prices for this currency and interval
              const matchingAddonPrices = stripeProductAddons
                .filter((addon: any) => !addon?.deletedAt)
                .flatMap((addon: any) =>
                  addon.stripePrices
                    .filter(
                      (price: any) =>
                        price.active &&
                        price.recurring.interval === 'month' &&
                        price.currency.toUpperCase() === currency
                    )
                    .map((price: any) => ({
                      id: price.id,
                      price:
                        (price.unit_amount || 100) /
                        (price.currency?.toUpperCase().toString() === 'JPY'
                          ? 1
                          : 100),
                      currency: price.currency.toUpperCase(),
                      metadata: price.metadata || {},
                    }))
                );

              if (matchingAddonPrices.length > 0) {
                price.addOnPrices = matchingAddonPrices;
              }
            });

            Object.entries(yearlyPrices).forEach(([currency, price]) => {
              // Find all addon prices for this currency and interval
              const matchingAddonPrices = stripeProductAddons
                .filter((addon: StripeProductAddon) => !addon?.deletedAt)
                .flatMap((addon: StripeProductAddon) =>
                  addon.stripePrices
                    .filter(
                      (price) =>
                        price.active &&
                        price.recurring.interval === 'year' &&
                        price.currency.toUpperCase() === currency
                    )
                    .map((price) => ({
                      id: price.id,
                      price:
                        (price.unit_amount || 100) /
                        (price.currency?.toUpperCase().toString() === 'JPY'
                          ? 1
                          : 100),
                      currency: price.currency.toUpperCase(),
                      metadata: price.metadata || {},
                    }))
                );

              if (matchingAddonPrices.length > 0) {
                price.addOnPrices = matchingAddonPrices;
              }
            });

            return {
              _id: subPlanItem._id,
              stripeProductId: subPlanItem.stripeProductId,
              limitations: subPlanItem.limitation,
              type: subPlanItem.type,
              description: subPlanItem.stripeProduct.description,
              name: subPlanItem.stripeProduct.name,
              metadata: subPlanItem.stripeProduct.metadata,
              monthly: monthlyPrices,
              yearly: yearlyPrices,
            };
          }) as PricingPlanMapped[];

          // Sort by USD yearly price if available, otherwise by first available yearly price
          const pricingPlansSorted = pricingPlanMapped.sort(
            (a: any, b: any) => {
              const aYearly = a.yearly || {};
              const bYearly = b.yearly || {};
              const aPrice =
                aYearly['USD']?.price ??
                (
                  Object.values(aYearly)[0] as
                    | RecurringIntervalPrice
                    | undefined
                )?.price ??
                0;
              const bPrice =
                bYearly['USD']?.price ??
                (
                  Object.values(bYearly)[0] as
                    | RecurringIntervalPrice
                    | undefined
                )?.price ??
                0;
              return aPrice - bPrice;
            }
          );

          this.localStorageService.setAllStripeProducts(pricingPlansSorted);

          return of(pricingPlansSorted as PricingPlanMapped[]);
        })
      );
    }
  }

  getStripeProductsAddons() {
    const localStripeAddOns = this.localStorageService.getAllStripeAddOns();
    if (localStripeAddOns.length > 0) {
      return of(localStripeAddOns);
    } else {
      return this.cm.get(`stripe/products/addons`).pipe(
        switchMap((stripeProductAddons: StripeProductAddon[]) => {
          const filterDeleted = stripeProductAddons.filter(
            (item) => !item?.deletedAt
          );

          const reMappedAddons = filterDeleted.map((item) => {
            const transformed: MappedAddOn = {
              _id: item._id,
              stripeProductId: item.stripeProductId,
              name: item.stripeProduct.name,
              yearly: {},
              monthly: {},
            };

            // Process all prices to find yearly and monthly options
            item.stripePrices.forEach((price: any) => {
              if (price.active) {
                const currency = price.currency.toUpperCase();
                const priceDetails: PriceDetails = {
                  id: price.id,
                  currency: currency,
                  recurring: price.recurring.interval,
                  price:
                    (price.unit_amount || 100) /
                    (price.currency?.toUpperCase().toString() === 'JPY'
                      ? 1
                      : 100),
                  unit_amount: price.unit_amount,
                  unit_amount_decimal: price.unit_amount_decimal,
                };

                if (price.recurring.interval === 'year') {
                  transformed.yearly[currency] = priceDetails;
                } else if (price.recurring.interval === 'month') {
                  transformed.monthly[currency] = priceDetails;
                }
              }
            });

            return transformed;
          });

          this.localStorageService.setAllStripeAddOns(reMappedAddons);

          return of(reMappedAddons);
        })
      );
    }
  }

  getAllUserSubscriptions(storeSubscriptions?: boolean) {
    const localStripeUserSubscriptions =
      this.localStorageService.getAllUserSubscriptions();
    if (localStripeUserSubscriptions.length > 0) {
      this.localStorageService.setAllUserSubscriptions([]);
      return of(localStripeUserSubscriptions);
    } else {
      return this.cm.get(`stripe/user/subscription`).pipe(
        switchMap((userSubscriptions: UserSubscription[]) => {
          const mappedSubscriptions = userSubscriptions.map(
            (userSubscription: UserSubscription) => {
              const isCanceled =
                userSubscription.stripeData.stripeSubscription
                  .cancel_at_period_end;
              const last4 =
                userSubscription.stripeData?.stripePaymentMethod?.card?.last4 ||
                '';
              const planInterval =
                userSubscription.stripeData.stripeSubscription?.plan
                  ?.interval || 'monthly';

              const statusBasedPlanEnd =
                userSubscription.status === StripeStatus.TRIALING
                  ? userSubscription.stripeData.stripeSubscription.trial_end
                  : userSubscription.stripeData.stripeSubscription
                      .current_period_end;

              const statusBasedPlanCanceledAt =
                userSubscription.status === StripeStatus.CANCELED
                  ? userSubscription.stripeData.stripeSubscription.canceled_at *
                    1000
                  : null;

              const cancellationRequested =
                userSubscription.stripeData.stripeSubscription
                  .cancellation_details.reason;
              const cancelAtPeriodEnd =
                userSubscription.stripeData.stripeSubscription
                  .current_period_end * 1000;

              const defaultPaymentMethodId = 
                userSubscription.stripeData.stripeSubscription.default_payment_method || null;

              const currentPeriodStart = statusBasedPlanEnd * 1000;
              const nextPaymentDate = new Date(currentPeriodStart);

              // Build pricingPlanMapped from the current subscription's product/price
              const stripeProduct = userSubscription.stripeData.stripeProduct;
              const stripePrice = userSubscription.stripeData.stripePrice;
              const limitations = {
                maxUserPerCompany: userSubscription.maxUserPerCompany,
                maxRealProjectsInCompany:
                  userSubscription.maxRealProjectsInCompany,
              };

              // Group price by interval and currency
              const monthly: Record<string, RecurringIntervalPrice> = {};
              const yearly: Record<string, RecurringIntervalPrice> = {};
              if (stripePrice) {
                // && stripePrice.active
                const currency = stripePrice.currency.toUpperCase();
                const priceObj: RecurringIntervalPrice = {
                  id: stripePrice.id,
                  price:
                    (stripePrice.unit_amount || 100) /
                    (currency === 'JPY' ? 1 : 100),
                  metadata: stripePrice.metadata,
                  currency: currency,
                };
                if (stripePrice.recurring?.interval === 'month') {
                  monthly[currency] = priceObj;
                } else if (stripePrice.recurring?.interval === 'year') {
                  yearly[currency] = priceObj;
                }
              }

              const pricingPlanMapped: PricingPlanMapped = {
                _id: stripeProduct.id,
                stripeProductId: stripeProduct.id,
                limitations: limitations,
                oldPrice: 0, // Set as needed
                newPrice: 0, // Set as needed
                name: stripeProduct.name,
                type: stripeProduct.type,
                description: stripeProduct.description,
                metadata: stripeProduct.metadata,
                monthly: monthly,
                yearly: yearly,
              };

              return {
                _id: userSubscription._id,
                status: userSubscription.status,
                company: userSubscription?.company || null,
                stripePriceId: userSubscription.stripePriceId,
                stripeSubscriptionId: userSubscription.stripeSubscriptionId,
                defaultPaymentMethodId: defaultPaymentMethodId,
                last4: last4,
                planInterval: planInterval,
                nextPaymentDate: nextPaymentDate,
                canceledDate: statusBasedPlanCanceledAt
                  ? new Date(statusBasedPlanCanceledAt)
                  : null,
                cancellationRequested: cancellationRequested,
                cancelAtPeriodEnd: cancelAtPeriodEnd,
                limitations: limitations,
                type: stripeProduct.type,
                description: stripeProduct.description,
                name: stripeProduct.name,
                isCanceled: isCanceled,
                metadata: {},
                pricingPlanMapped: pricingPlanMapped,
              };
            }
          );

          // const filterCanceled = mappedSubscriptions.filter(
          //   (mSubItem) =>
          //     mSubItem.status !== StripeStatus.CANCELED ||
          //     (mSubItem.status === StripeStatus.CANCELED &&
          //       mSubItem.company !== null)
          // );

          const mapCanceled = mappedSubscriptions.map((mSubItem) => {
            if (mSubItem.status === StripeStatus.CANCELED) {
              const countSameCompany = mappedSubscriptions.filter(
                (dupCmp) =>
                  dupCmp.company === mSubItem.company &&
                  dupCmp.status !== StripeStatus.CANCELED
              );
              if (countSameCompany.length) {
                return {
                  ...mSubItem,
                  withActiveSubscription: true,
                };
              }

              if (!mSubItem?.company) {
                return {
                  ...mSubItem,
                  withActiveSubscription: true,
                };
              }

              return {
                ...mSubItem,
                withActiveSubscription: false,
              };
            }

            return mSubItem;
          });
          if (storeSubscriptions) {
            this.localStorageService.setAllUserSubscriptions(mapCanceled);
          }

          return of(mapCanceled);
        })
      );
    }
  }

  getUserSubscriptions(companyId: string) {
    return this.cm.get(`stripe/user/subscription`).pipe(
      switchMap((userSubscriptions: UserSubscription[]) => {
        const companyFiltered = userSubscriptions.find((userSubsc) => {
          return userSubsc.company === companyId;
        });

        if (companyFiltered) {
          const isCanceled =
            companyFiltered.stripeData.stripeSubscription.cancel_at_period_end;
          const last4 =
            companyFiltered.stripeData?.stripePaymentMethod?.card?.last4 || '';
          const planInterval =
            companyFiltered.stripeData.stripeSubscription.plan.interval;

          const statusBasedPlanEnd =
            companyFiltered.status === StripeStatus.TRIALING
              ? companyFiltered.stripeData.stripeSubscription.trial_end
              : companyFiltered.stripeData.stripeSubscription
                  .current_period_end;

          const statusBasedPlanCanceledAt =
            companyFiltered.status === StripeStatus.CANCELED
              ? companyFiltered.stripeData.stripeSubscription.canceled_at * 1000
              : null;

          const currentPeriodStart = statusBasedPlanEnd * 1000;
          const nextPaymentDate = new Date(currentPeriodStart);

          const wrappedUserSubscription = {
            _id: companyFiltered._id,
            stripePriceId: companyFiltered.stripePriceId,
            stripeSubscriptionId: companyFiltered.stripeSubscriptionId,
            last4: last4,
            planInterval: planInterval,
            nextPaymentDate: nextPaymentDate,
            canceledDate: statusBasedPlanCanceledAt
              ? new Date(statusBasedPlanCanceledAt)
              : null,
            limitations: {
              maxUserPerCompany: companyFiltered.maxUserPerCompany,
              maxRealProjectsInCompany:
                companyFiltered.maxRealProjectsInCompany,
            },
            type: companyFiltered.stripeData.stripeProduct.type,
            description: companyFiltered.stripeData.stripeProduct.description,
            name: companyFiltered.stripeData.stripeProduct.name,
            isCanceled: isCanceled,
            metadata: {},
          };

          return of(wrappedUserSubscription);
        }

        return of(null);
      })
    );
  }

  getActiveCompoanySubscription() {
    const companyId = this.localStorageService.getCurrentCompany()?._id;
    return this.cm.get(`stripe/company/subscription`, [
      { headerName: 'companyId', headerValue: companyId || '' },
    ]);
  }

  changeUserSubscription(subscriptionId: string, priceId: string) {
    const payload = {
      priceId,
    };
    return this.cm.post(
      `stripe/subscription/${subscriptionId}/change`,
      payload
    );
  }

  cancelUserSubscriptionById(subscriptionId: string) {
    return this.cm.get(`stripe/subscription/${subscriptionId}/cancel`);
  }

  getUserSubscriptionById(subscriptionId: string) {
    return this.cm.get(`stripe/subscription/${subscriptionId}`).pipe(
      switchMap((userSubscription: UserSubscription) => {
  
          const isCanceled =
            userSubscription.stripeData.stripeSubscription
              .cancel_at_period_end;
          const last4 =
            userSubscription.stripeData?.stripePaymentMethod?.card?.last4 ||
            '';
          const planInterval =
            userSubscription.stripeData.stripeSubscription?.plan
              ?.interval || 'monthly';

          const statusBasedPlanEnd =
            userSubscription.status === StripeStatus.TRIALING
              ? userSubscription.stripeData.stripeSubscription.trial_end
              : userSubscription.stripeData.stripeSubscription
                  .current_period_end;

          const statusBasedPlanCanceledAt =
            userSubscription.status === StripeStatus.CANCELED
              ? userSubscription.stripeData.stripeSubscription.canceled_at *
                1000
              : null;

          const cancellationRequested =
            userSubscription.stripeData.stripeSubscription
              .cancellation_details.reason;
          const cancelAtPeriodEnd =
            userSubscription.stripeData.stripeSubscription
              .current_period_end * 1000;

          const defaultPaymentMethodId = 
            userSubscription.stripeData.stripeSubscription.default_payment_method || null;

          const currentPeriodStart = statusBasedPlanEnd * 1000;
          const nextPaymentDate = new Date(currentPeriodStart);

          // Build pricingPlanMapped from the current subscription's product/price
          const stripeProduct = userSubscription.stripeData.stripeProduct;
          const stripePrice = userSubscription.stripeData.stripePrice;
          const limitations = {
            maxUserPerCompany: userSubscription.maxUserPerCompany,
            maxRealProjectsInCompany:
              userSubscription.maxRealProjectsInCompany,
          };

          // Group price by interval and currency
          const monthly: Record<string, RecurringIntervalPrice> = {};
          const yearly: Record<string, RecurringIntervalPrice> = {};
          if (stripePrice) {
            // && stripePrice.active
            const currency = stripePrice.currency.toUpperCase();
            const priceObj: RecurringIntervalPrice = {
              id: stripePrice.id,
              price:
                (stripePrice.unit_amount || 100) /
                (currency === 'JPY' ? 1 : 100),
              metadata: stripePrice.metadata,
              currency: currency,
            };
            if (stripePrice.recurring?.interval === 'month') {
              monthly[currency] = priceObj;
            } else if (stripePrice.recurring?.interval === 'year') {
              yearly[currency] = priceObj;
            }
          }

          const pricingPlanMapped: PricingPlanMapped = {
            _id: stripeProduct.id,
            stripeProductId: stripeProduct.id,
            limitations: limitations,
            oldPrice: 0, // Set as needed
            newPrice: 0, // Set as needed
            name: stripeProduct.name,
            type: stripeProduct.type,
            description: stripeProduct.description,
            metadata: stripeProduct.metadata,
            monthly: monthly,
            yearly: yearly,
          };

          const wrappedUserSubscription = {
            _id: userSubscription._id,
            status: userSubscription.status,
            company: userSubscription?.company || null,
            stripePriceId: userSubscription.stripePriceId,
            stripeSubscriptionId: userSubscription.stripeSubscriptionId,
            defaultPaymentMethodId: defaultPaymentMethodId,
            last4: last4,
            planInterval: planInterval,
            nextPaymentDate: nextPaymentDate,
            canceledDate: statusBasedPlanCanceledAt
              ? new Date(statusBasedPlanCanceledAt)
              : null,
            cancellationRequested: cancellationRequested,
            cancelAtPeriodEnd: cancelAtPeriodEnd,
            limitations: limitations,
            type: stripeProduct.type,
            description: stripeProduct.description,
            name: stripeProduct.name,
            isCanceled: isCanceled,
            metadata: {},
            pricingPlanMapped: pricingPlanMapped,
          };
          return of(wrappedUserSubscription);
      })
    );
  }

  postStripePlainId(stripePriceId: string, companyId?: string) {
    const payload: any = {
      stripePriceId,
    };

    if (companyId) {
      payload.companyId = companyId;
    }
    return this.cm.post('stripe/session', payload);
  }

  getSubscriptionCurrency(
    stripePriceId: string,
    subscriptionPlans: PricingPlanMapped[]
  ): string | null {
    for (const plan of subscriptionPlans) {
      // Check monthly prices
      for (const [currency, price] of Object.entries(plan.monthly)) {
        if (price.id === stripePriceId) {
          return currency;
        }
      }
      // Check yearly prices
      for (const [currency, price] of Object.entries(plan.yearly)) {
        if (price.id === stripePriceId) {
          return currency;
        }
      }
    }
    return null;
  }

  stripeSubscriptionResume(subscriptionId: string) {
    return this.cm.post(`stripe/subscription/${subscriptionId}/resume-canceled`);
  }

  stripeAddPaymentMethod(subscriptionId?: string) {
    return this.cm.get(`stripe/add-payment-method/${subscriptionId || ''}`);
  }
}
